import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { combineLatest, of } from 'rxjs';
import {
  map,
  mergeMap,
  catchError,
  switchMap,
  filter,
  tap,
} from 'rxjs/operators';
import { EnvService } from '../../services/env.service';
import { GetCountriesSuccess, CountriesActions } from './countries.actions';
import { select, Store } from '@ngrx/store';
import { selectLocale } from '../query-params';
import { getCurrentLabelsJson } from '../labels';
import { defaultCountry, GetServiceOptionsSuccess } from '../service-options';
import { GetStates } from '../states';
import { config as countryConfig } from '../../config/country-config';
import { AppCountry, CmsCountryObj, StatesObj } from '../../app.models';
import { CountryDetails } from './countries.model';
import { statesAllowedIso3Countries } from 'src/app/config/app-config';
import { ClearSelectedLocationAction } from '../transaction/transaction.actions';
import { ClearFeeQuoteAction } from '../fee-quote/fee-quote.actions';

@Injectable()
export class CountriesEffects {
  constructor(
    private actions$: Actions,
    private http: HttpClient,
    private envService: EnvService,
    private store: Store
  ) {}

  private countriesUrl(endpoint: string): string {
    return endpoint === 'v3' // TODO:TIM Requirements changed, so this can probably be refactored into a single string value, and just interpolate the v2 or v3
      ? `${this.envService.apiUrl}/v3/reference-data/countries`
      : `${this.envService.apiUrl}/v2/reference-data/countries`;
  }

  public getCountries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetServiceOptionsSuccess),
      tap(() => this.store.dispatch(ClearSelectedLocationAction())),
      tap(() => this.store.dispatch(ClearFeeQuoteAction())),
      switchMap((action) =>
        combineLatest([
          of(action.serviceOptionsResponse),
          of(action.endpoint),
          this.store.pipe(select(getCurrentLabelsJson)),
          this.store.pipe(select(selectLocale)),
          this.store.pipe(select(defaultCountry)),
        ])
      ),
      filter(
        ([_, endpoint, currentLabels, locale, defaultCountry]) =>
          !!defaultCountry
      ),
      mergeMap(([_, endpoint, currentLabels, locale, defaultCountry]) => {
        return this.http.get(this.countriesUrl(endpoint)).pipe(
          map((countryData: any) => {
            const labels: any = currentLabels?.labels;
            const countriesCMS = labels?.stellar?.country;
            if (countriesCMS) {
              let countries = this.mapCountriesData1(
                countryData?.countries,
                countriesCMS
              );
              countries?.sort((a: any, b: any) => a.name.localeCompare(b.name));
              countries = this.alphabetizeStateNames(countries);
              countryData.countries = countries;
            }
            this.dispatchStates(defaultCountry, locale, endpoint);
            return GetCountriesSuccess({ countriesResponse: countryData });
          }),
          catchError((error) => {
            const labels: any = currentLabels?.labels;
            const countriesCMS = labels?.stellar?.country;
            this.dispatchStates(defaultCountry, locale, endpoint);
            return of({
              type: CountriesActions.getCountriesFailed,
              payload: {
                error,
                countries: this.mapCountriesData1(countryConfig, countriesCMS),
              },
            });
          })
        );
      })
    )
  );

  alphabetizeStateNames(countries: any[]): any[] {
    const supportsStates = statesAllowedIso3Countries || ['USA', 'CAN', 'MEX'];
    for (let countryCode of supportsStates) {
      let indexOfCountry = countries.findIndex(
        (country: any) => country.code === countryCode
      );
      if (indexOfCountry > -1) {
        countries[indexOfCountry].states.sort((a: any, b: any) =>
          a.name.localeCompare(b.name)
        );
      }
    }
    return countries;
  }

  mapCountriesData(countries: AppCountry[], countriesCMS: any) {
    // In case any error countries should be used from local config and map using CMS content
    if (countries && countries.length > 0 && countriesCMS) {
      countries = countries.map((country: any) => {
        const countryItem: CountryDetails =
          countriesCMS[country.iso3Code] || countriesCMS[country.code];
        if (countryItem) {
          country.name = countryItem.name;
          country.iso3Code = countryItem?.code || countryItem?.iso3Code;
        }
        return country;
      });
      return countries;
    }
    return countryConfig;
  }

  mapCountriesData1(countries: AppCountry[], countriesCMS: CmsCountryObj[]) {
    // In case any error countries should be used from local config and map using CMS content
    if (countries && countries.length > 0 && countriesCMS) {
      const cmsCountries = countries.map((country: any) => {
        let countryItem: CmsCountryObj;
        // If a country is provided in the Countries API response that is NOT present in the Translation CMS, UI displays that country's info as-is (however it is received from the API)
        if (countriesCMS[country.iso3Code] || countriesCMS[country.code]) {
          countryItem =
            countriesCMS[country.iso3Code] || countriesCMS[country.code];
        } else {
          countryItem = {
            iso2code: country.iso2Code,
            name: country.name,
            code: country.iso3Code,
            states: [],
          };
        }

        if (countryItem) {
          countryItem = {
            ...countryItem,
            states: this.mapStates(countryItem?.states),
          };
        }
        return countryItem;
      });
      return cmsCountries;
    }
    return this.cmsCountriesMapping(countriesCMS);
  }

  cmsCountriesMapping(countriesCMS: CmsCountryObj[]): any {
    countriesCMS = countriesCMS.map((country) => {
      country.states = this.mapStates(country.states);
      return country;
    });

    return countriesCMS;
  }

  mapStates(statesObj: any): StatesObj[] {
    if (statesObj) {
      const satesArray: StatesObj[] = [];
      for (const [key, value] of Object.entries(statesObj)) {
        satesArray.push({
          code: key,
          name: value,
        });
      }
      return satesArray;
    }
    return [];
  }

  dispatchStates(defaultCountry: string, locale: string, endpoint: string) {
    const payload = {
      country: defaultCountry,
      userLanguage: locale,
    };
    this.store.dispatch(GetStates({ getStatesRequest: payload, endpoint }));
  }
}
